import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, AlertTriangle, Clock, Users, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const IncidentResponsePolicyEN = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 20, 2024";
  const companyName = "EasyFinder";
  const securityEmail = "security@easyfinder.fr";
  const dpoEmail = "dpo@easyfinder.fr";

  return (
    <>
      <SEO
        title="Incident Response Policy | EasyFinder"
        description="EasyFinder Security Incident Response Policy. Detection, investigation, and resolution procedures for security incidents."
        canonical="/incident-response-policy-en"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <LanguageSwitcher frPath="/incident-response-policy" enPath="/incident-response-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold mb-0">Security Incident Response Policy</h1>
            </div>
            <p className="text-muted-foreground mb-8">
              Last updated: {lastUpdated}
            </p>

            {/* Purpose */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-6 w-6" />
                1. Purpose
              </h2>
              <p>
                This policy defines the procedures for detecting, reporting, investigating, and resolving 
                security incidents affecting {companyName} and its users.
              </p>
              <p>
                The objective is to ensure a rapid, coordinated, and effective response to any incident 
                that may compromise the confidentiality, integrity, or availability of {companyName}'s data and systems.
              </p>
            </section>

            {/* Scope */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Scope</h2>
              <p>This policy applies to:</p>
              <ul>
                <li>All {companyName} systems and infrastructure (web application, API, database)</li>
                <li>All employees, contractors, and partners with system access</li>
                <li>All sub-processors handling data on behalf of {companyName}</li>
                <li>User personal data in compliance with GDPR</li>
              </ul>
              
              <h3 className="text-xl font-medium mt-6 mb-3">Covered Systems</h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">System</th>
                    <th className="border border-border p-3 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Web Application</td>
                    <td className="border border-border p-3">easyfinder.fr - Main user interface</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Backend API</td>
                    <td className="border border-border p-3">Supabase Edge Functions - Business logic</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Database</td>
                    <td className="border border-border p-3">PostgreSQL (Supabase) - Data storage</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">File Storage</td>
                    <td className="border border-border p-3">Supabase Storage - User documents</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Authentication</td>
                    <td className="border border-border p-3">Supabase Auth + Google OAuth</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">CDN / DNS</td>
                    <td className="border border-border p-3">Cloudflare - Distribution and protection</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Roles and Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Users className="h-6 w-6" />
                3. Roles and Responsibilities
              </h2>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-2">Incident Response Team (IRT)</h3>
                <table className="w-full">
                  <tbody>
                    <tr>
                      <td className="py-2 font-medium">Security Officer</td>
                      <td className="py-2">{securityEmail}</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium">Data Protection Officer (DPO)</td>
                      <td className="py-2">{dpoEmail}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-lg font-medium mt-4 mb-2">Security Officer</h4>
              <ul>
                <li>Coordinate all incident response activities</li>
                <li>Make critical decisions (isolation, restoration, notification)</li>
                <li>Communicate with management and stakeholders</li>
                <li>Supervise forensic analysis and documentation</li>
              </ul>

              <h4 className="text-lg font-medium mt-4 mb-2">Data Protection Officer (DPO)</h4>
              <ul>
                <li>Assess impact on personal data (GDPR)</li>
                <li>Prepare supervisory authority notifications (within 72h if required)</li>
                <li>Coordinate communication to affected individuals</li>
                <li>Document data breaches</li>
              </ul>

              <h4 className="text-lg font-medium mt-4 mb-2">Technical Team</h4>
              <ul>
                <li>Detect and analyze indicators of compromise</li>
                <li>Isolate affected systems</li>
                <li>Collect evidence (logs, captures)</li>
                <li>Implement fixes and restore services</li>
              </ul>
            </section>

            {/* Incident Classification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                4. Incident Classification
              </h2>

              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Level</th>
                    <th className="border border-border p-3 text-left">Description</th>
                    <th className="border border-border p-3 text-left">Examples</th>
                    <th className="border border-border p-3 text-left">Response Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-red-50 dark:bg-red-950">
                    <td className="border border-border p-3 font-bold text-red-600 dark:text-red-400">🔴 Critical (P1)</td>
                    <td className="border border-border p-3">Active compromise, confirmed data breach</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Unauthorized database access</li>
                        <li>Personal data leak</li>
                        <li>Ransomware</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">Immediate (≤1h)</td>
                  </tr>
                  <tr className="bg-orange-50 dark:bg-orange-950">
                    <td className="border border-border p-3 font-bold text-orange-600 dark:text-orange-400">🟠 High (P2)</td>
                    <td className="border border-border p-3">Intrusion attempt, exploitable vulnerability</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Ongoing DDoS attack</li>
                        <li>Brute force attempts</li>
                        <li>0-day vulnerability discovered</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤4h</td>
                  </tr>
                  <tr className="bg-yellow-50 dark:bg-yellow-950">
                    <td className="border border-border p-3 font-bold text-yellow-600 dark:text-yellow-400">🟡 Medium (P3)</td>
                    <td className="border border-border p-3">Suspicious activity, anomaly detected</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Authentication error spike</li>
                        <li>Port scan detected</li>
                        <li>Abnormal user behavior</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤24h</td>
                  </tr>
                  <tr className="bg-blue-50 dark:bg-blue-950">
                    <td className="border border-border p-3 font-bold text-blue-600 dark:text-blue-400">🔵 Low (P4)</td>
                    <td className="border border-border p-3">Minor incident, no user impact</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Phishing email reported</li>
                        <li>Confirmed false alarm</li>
                        <li>Security update required</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤72h</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Response Procedure */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-6 w-6" />
                5. Incident Response Procedure
              </h2>

              <div className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    Detection and Reporting
                  </h3>
                  <ul>
                    <li>All suspected incidents must be reported immediately to <strong>{securityEmail}</strong></li>
                    <li>Reports should include: date/time, description, affected systems, actions already taken</li>
                    <li>Do NOT attempt to fix the incident without Security Officer validation</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                    Triage and Classification
                  </h3>
                  <ul>
                    <li>Evaluate the nature and scope of the incident</li>
                    <li>Classify according to severity level (P1-P4)</li>
                    <li>Identify affected systems, data, and users</li>
                    <li>Determine if personal data is involved (GDPR)</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                    Containment
                  </h3>
                  <ul>
                    <li>Isolate compromised systems (block network access, disable accounts)</li>
                    <li>Preserve evidence (log captures, snapshots)</li>
                    <li>Revoke suspicious tokens/sessions</li>
                    <li>Document all actions taken with timestamps</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                    Investigation and Analysis
                  </h3>
                  <ul>
                    <li>Analyze logs (audit_logs, auth logs, edge function logs)</li>
                    <li>Identify attack vector and timeline</li>
                    <li>Assess extent of compromise</li>
                    <li>Determine root cause</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                    Eradication and Remediation
                  </h3>
                  <ul>
                    <li>Remove attack vector (malware, backdoor, compromised account)</li>
                    <li>Apply necessary security patches</li>
                    <li>Reset exposed credentials</li>
                    <li>Strengthen security controls (RLS policies, input validation)</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">6</span>
                    Recovery
                  </h3>
                  <ul>
                    <li>Restore systems from clean backups if necessary</li>
                    <li>Validate data integrity</li>
                    <li>Gradually restore services to production</li>
                    <li>Closely monitor restored systems</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">7</span>
                    Post-Incident Review
                  </h3>
                  <ul>
                    <li>Conduct post-mortem analysis</li>
                    <li>Document lessons learned</li>
                    <li>Update procedures and controls</li>
                    <li>Share findings with relevant stakeholders</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Communication */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Communication Plan</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">Regulatory Notifications (GDPR)</h3>
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">
                  In case of a personal data breach likely to result in a risk to the rights and freedoms of individuals:
                </p>
                <ul className="text-red-700 dark:text-red-300 mt-2">
                  <li><strong>Supervisory Authority:</strong> Notification within 72 hours (CNIL in France)</li>
                  <li><strong>Affected Users:</strong> Without undue delay if high risk to individuals</li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Emergency Contacts</h2>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <ul>
                  <li><strong>Security Team:</strong> <a href={`mailto:${securityEmail}`} className="text-primary underline">{securityEmail}</a></li>
                  <li><strong>Data Protection Officer:</strong> <a href={`mailto:${dpoEmail}`} className="text-primary underline">{dpoEmail}</a></li>
                </ul>
              </div>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentResponsePolicyEN;
